import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import useApi from '../hooks/useApi';
import haxios from '../lib/haxios';
import { 
  BuildingOfficeIcon, 
  ArrowLeftIcon,
  PencilIcon,
  UsersIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  TargetIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatPercentage } from '../components/AdvancedReports/shared/utils';
import ReportKPICards from '../components/AdvancedReports/core/ReportKPICards';
import DataTable from '../components/AdvancedReports/shared/DataTable';
import clsx from 'clsx';
import moment from 'moment';

const BranchDetail = () => {
  const { branchCode } = useParams();
  const navigate = useNavigate();
  const { uinfo } = useContext(GlobalContext);
  const [capacityData, setCapacityData] = useState([]);
  const [loadingCapacity, setLoadingCapacity] = useState(false);
  const [editingCapacity, setEditingCapacity] = useState(null);

  // Fetch branch data
  const { data: branches = [], isLoading: branchesLoading } = useApi({ 
    url: '/salesassist/cplusassist/branches', 
    initial: [] 
  });

  // Fetch staff data
  const { data: allStaffData = [], isLoading: staffLoading } = useApi({ 
    url: '/salesassistnew/ustafferators', 
    initial: [] 
  });

  // Fetch year-to-date sales data for this branch
  const [branchPerformance, setBranchPerformance] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(true);

  const branch = useMemo(() => {
    return branches.find(b => (b.MamCode || b.Branch) === branchCode);
  }, [branches, branchCode]);

  // Calculate actual staff counts by category
  const actualStaffCounts = useMemo(() => {
    if (!branchCode || !allStaffData || allStaffData.length === 0) return {};
    
    const branchStaff = allStaffData.filter(s => s.Branch === branchCode);
    const counts = {};
    
    const categories = ['Manager', 'Biker', 'Warehouse', 'Sales Staff', 'Admin', 'Other'];
    categories.forEach(cat => {
      counts[cat] = branchStaff.filter(s => s.StaffCategory === cat).length;
    });
    
    return counts;
  }, [branchCode, allStaffData]);

  // Fetch capacity planning data
  useEffect(() => {
    const fetchCapacity = async () => {
      if (!branchCode) return;
      setLoadingCapacity(true);
      try {
        // TODO: Replace with actual API endpoint when backend is ready
        // For now, use mock data structure
        const response = await haxios.get(`/api/branch-capacity/${branchCode}`).catch(() => null);
        
        if (response && Array.isArray(response)) {
          setCapacityData(response);
        } else {
          // Initialize with default structure if no data exists
          const categories = ['Manager', 'Biker', 'Warehouse', 'Sales Staff', 'Admin', 'Other'];
          setCapacityData(categories.map(cat => ({
            branch_code: branchCode,
            role_category: cat,
            target_count: 0,
            actual_count: actualStaffCounts[cat] || 0,
            notes: ''
          })));
        }
      } catch (error) {
        console.error('Error fetching capacity data:', error);
        // Initialize with defaults on error
        const categories = ['Manager', 'Biker', 'Warehouse', 'Sales Staff', 'Admin', 'Other'];
        setCapacityData(categories.map(cat => ({
          branch_code: branchCode,
          role_category: cat,
          target_count: 0,
          actual_count: actualStaffCounts[cat] || 0,
          notes: ''
        })));
      } finally {
        setLoadingCapacity(false);
      }
    };

    if (branchCode) {
      fetchCapacity();
    }
  }, [branchCode, actualStaffCounts]);

  // Update actual counts when staff data changes
  useEffect(() => {
    if (capacityData.length > 0) {
      setCapacityData(prev => prev.map(item => ({
        ...item,
        actual_count: actualStaffCounts[item.role_category] || 0
      })));
    }
  }, [actualStaffCounts]);

  // Fetch year-to-date performance
  useEffect(() => {
    const fetchPerformance = async () => {
      if (!branchCode) return;
      setLoadingPerformance(true);
      try {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        const monthPromises = [];
        for (let month = 1; month <= currentMonth; month++) {
          monthPromises.push(
            haxios.get(`/visiondashboard/salesassist/getSales/${currentYear}/${month}/${branchCode}`)
              .catch(() => [])
          );
        }

        const monthlyResults = await Promise.all(monthPromises);
        
        let totalSales = 0;
        let totalCost = 0;
        let totalCredits = 0;
        let invoiceCount = 0;

        monthlyResults.forEach(monthData => {
          if (Array.isArray(monthData) && monthData.length > 0) {
            monthData.forEach(day => {
              totalSales += parseFloat(day.sales || 0);
              totalCost += parseFloat(day.cost || 0);
              totalCredits += parseFloat(day.credits || 0);
              invoiceCount += parseInt(day.invoices || 0);
            });
          }
        });

        const totalMargin = totalSales - totalCost;
        const netSales = totalSales + totalCredits;
        const marginPercent = totalSales > 0 ? (totalMargin / totalSales) * 100 : 0;

        setBranchPerformance({
          totalSales,
          netSales,
          totalCost,
          totalMargin,
          totalCredits,
          marginPercent,
          invoiceCount
        });
      } catch (error) {
        console.error('Error fetching performance data:', error);
      } finally {
        setLoadingPerformance(false);
      }
    };

    if (branchCode) {
      fetchPerformance();
    }
  }, [branchCode]);

  // Calculate staff costs
  const staffCosts = useMemo(() => {
    if (!branchCode || !allStaffData) return { totalCost: 0, staffCount: 0 };
    
    const branchStaff = allStaffData.filter(s => s.Branch === branchCode);
    const totalCost = branchStaff.reduce((sum, staff) => 
      sum + (parseFloat(staff.MonthlyCostPerHead) || 0), 0
    );
    
    return {
      totalCost,
      staffCount: branchStaff.length
    };
  }, [branchCode, allStaffData]);

  // KPI data
  const kpiData = useMemo(() => {
    if (!branchPerformance) return [];
    
    return [
      {
        key: 'total-sales',
        label: 'Total Sales (YTD)',
        value: branchPerformance.totalSales,
        type: 'sales',
        format: 'currency'
      },
      {
        key: 'net-sales',
        label: 'Net Sales (YTD)',
        value: branchPerformance.netSales,
        type: 'sales',
        format: 'currency'
      },
      {
        key: 'margin',
        label: 'Margin (YTD)',
        value: branchPerformance.totalMargin,
        type: 'margin',
        format: 'currency',
        color: branchPerformance.marginPercent < 0 ? 'text-red-600' : 'text-green-600'
      },
      {
        key: 'margin-percent',
        label: 'Margin %',
        value: branchPerformance.marginPercent,
        type: 'margin',
        format: 'percentage',
        color: branchPerformance.marginPercent < 0 ? 'text-red-600' : 'text-green-600'
      },
      {
        key: 'invoices',
        label: 'Invoices (YTD)',
        value: branchPerformance.invoiceCount,
        type: 'customers',
        format: 'number'
      },
      {
        key: 'staff-costs',
        label: 'Staff Costs (Monthly)',
        value: staffCosts.totalCost,
        type: 'cost',
        format: 'currency',
        subtitle: `${staffCosts.staffCount} staff members`
      }
    ];
  }, [branchPerformance, staffCosts]);

  // Staff table data
  const branchStaff = useMemo(() => {
    if (!branchCode || !allStaffData) return [];
    return allStaffData.filter(s => s.Branch === branchCode);
  }, [branchCode, allStaffData]);

  const staffColumns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (
        <span className="font-medium text-gray-900">
          {`${row.FName || ''} ${row.SName || ''}`.trim() || 'N/A'}
        </span>
      )
    },
    { key: 'Position', label: 'Position', sortable: true },
    { key: 'StaffCategory', label: 'Category', sortable: true },
    {
      key: 'MonthlyCostPerHead',
      label: 'Monthly Cost',
      sortable: true,
      render: (value) => value ? formatCurrency(value) : '—'
    },
    {
      key: 'SalesTarget',
      label: 'Sales Target',
      sortable: true,
      render: (value) => value ? formatCurrency(value) : '—'
    }
  ];

  const handleSaveCapacity = async (item) => {
    try {
      // TODO: Replace with actual API endpoint when backend is ready
      const endpoint = item.id 
        ? `/api/branch-capacity/${item.id}`
        : `/api/branch-capacity`;
      
      const method = item.id ? 'PUT' : 'POST';
      
      await haxios[method.toLowerCase()](endpoint, {
        branch_code: branchCode,
        role_category: item.role_category,
        target_count: parseInt(item.target_count),
        notes: item.notes || ''
      });
      
      setEditingCapacity(null);
      // Refresh capacity data
      const response = await haxios.get(`/api/branch-capacity/${branchCode}`).catch(() => null);
      if (response && Array.isArray(response)) {
        setCapacityData(response);
      }
    } catch (error) {
      console.error('Error saving capacity:', error);
    }
  };

  if (branchesLoading || !branch) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading branch...</span>
        </div>
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="px-6 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Branch not found</p>
            <button
              onClick={() => navigate('/settings/branch')}
              className="mt-2 text-sm text-red-600 hover:text-red-800"
            >
              Return to Branch Settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/settings/branch')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <BuildingOfficeIcon className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{branch.Branch}</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {branch.MamCode} • {branch.PCode}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate(`/settings/branch?edit=${branch._id}`)}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <PencilIcon className="w-5 h-5" />
            <span>Edit Branch</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-6 px-6 space-y-6">
        {/* KPI Cards */}
        {loadingPerformance ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-1/4"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <ReportKPICards kpis={kpiData} loading={loadingPerformance} />
        )}

        {/* Capacity Planning Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                <UsersIcon className="w-6 h-6 text-indigo-600" />
                <span>Capacity Planning</span>
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Target vs actual staff counts by role category
              </p>
            </div>
          </div>

          {loadingCapacity ? (
            <div className="animate-pulse space-y-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {capacityData.map((item, index) => {
                const isEditing = editingCapacity === index;
                const isOnTarget = item.actual_count >= item.target_count;
                const variance = item.actual_count - item.target_count;

                return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
                  >
                    {isEditing ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900">{item.role_category}</h3>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSaveCapacity(item)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingCapacity(null)}
                              className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Target Count
                            </label>
                            <input
                              type="number"
                              value={item.target_count}
                              onChange={(e) => {
                                const updated = [...capacityData];
                                updated[index].target_count = parseInt(e.target.value) || 0;
                                setCapacityData(updated);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              min="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Notes
                            </label>
                            <input
                              type="text"
                              value={item.notes || ''}
                              onChange={(e) => {
                                const updated = [...capacityData];
                                updated[index].notes = e.target.value;
                                setCapacityData(updated);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              placeholder="Optional notes..."
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="w-32">
                            <h3 className="font-semibold text-gray-900">{item.role_category}</h3>
                          </div>
                          <div className="flex items-center space-x-6 flex-1">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-gray-900">
                                {item.actual_count}
                              </div>
                              <div className="text-xs text-gray-500">Actual</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-indigo-600">
                                {item.target_count}
                              </div>
                              <div className="text-xs text-gray-500">Target</div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {isOnTarget ? (
                                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                              ) : (
                                <XCircleIcon className="w-5 h-5 text-red-500" />
                              )}
                              <span className={clsx(
                                'text-sm font-medium',
                                isOnTarget ? 'text-green-600' : 'text-red-600'
                              )}>
                                {variance >= 0 ? '+' : ''}{variance}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setEditingCapacity(index)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Staff List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <UsersIcon className="w-6 h-6 text-indigo-600" />
            <span>Staff Members ({branchStaff.length})</span>
          </h2>
          <DataTable
            data={branchStaff}
            loading={staffLoading}
            columns={staffColumns}
            searchable={true}
            sortable={true}
            pagination={true}
            pageSize={10}
            emptyMessage="No staff members found for this branch"
          />
        </div>
      </div>
    </div>
  );
};

export default BranchDetail;

